import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/pages/home/components/VideoCard';
import { videos, categories } from '@/mocks/homeData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState<'newest' | 'popular' | 'rating'>('newest');

  const category = categories.find((c) => c.slug === slug);

  const categoryVideos = useMemo(() => {
    const filtered = videos.filter((v) => v.categorySlug === slug);
    if (activeSort === 'popular') {
      return [...filtered].sort((a, b) => parseInt(b.views.replace(/[万.]/g, '')) - parseInt(a.views.replace(/[万.]/g, '')));
    }
    if (activeSort === 'rating') {
      return [...filtered].sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
    }
    return [...filtered].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [slug, activeSort]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100/50 border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: category?.name || slug || '' }]} />
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mt-2">
              {category?.name || '分类'}
            </h1>
            {category?.description && (
              <p className="text-sm text-foreground-600 mt-2">{category.description}</p>
            )}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground-600 mr-1">分类切换：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 text-sm rounded-full transition-all duration-200 whitespace-nowrap ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-900">{categoryVideos.length}</strong> 个视频
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[
                { key: 'newest', label: '最新' },
                { key: 'popular', label: '最热' },
                { key: 'rating', label: '评分' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setActiveSort(s.key as typeof activeSort)}
                  className={`px-4 py-1.5 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSort === s.key
                      ? 'bg-white text-foreground-950 font-medium shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pb-14">
          {categoryVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {categoryVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-video-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无内容</p>
              <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 mt-2 inline-block">返回首页</Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}